import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getProductReviews, createProductReview, updateProductReview, deleteProductReview, getProductCategories, getPhotos } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { Package, Plus, Trash2, Edit, X, Loader2, Check, Star, Camera, Sparkles, ThumbsUp, ThumbsDown, Award, Clock, ExternalLink, ChevronDown, ChevronUp, DollarSign } from "lucide-react";

const defaultCategories = [
  { value: "electronics", label: "Électronique voyage", icon: "📱" },
  { value: "luggage", label: "Bagagerie", icon: "🧳" },
  { value: "photo", label: "Photo & Vidéo", icon: "📸" },
  { value: "accessories", label: "Accessoires", icon: "🎒" },
  { value: "comfort", label: "Confort & Bien-être", icon: "💤" },
  { value: "outdoor", label: "Outdoor & Sport", icon: "⛰️" },
  { value: "books", label: "Guides & Livres", icon: "📚" },
  { value: "apps", label: "Apps & Services", icon: "💻" },
];

export default function ProductReviewsAdminPage() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [categories, setCategories] = useState(defaultCategories);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showPhotoLibrary, setShowPhotoLibrary] = useState(false);
  const [expandedReview, setExpandedReview] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  const [form, setForm] = useState({
    title: "", category: "electronics", brand: "", model_name: "",
    test_context: "", test_duration: "", usage_conditions: "", target_audience: "",
    description: "", rating: 4, value_rating: 4, pros: "", cons: "",
    overall_impression: "", verdict: "", practical_tips: "", alternatives: "",
    price: "", purchase_link: "", is_published: false
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [reviewsRes, categoriesRes, photosRes] = await Promise.all([
        getProductReviews({ published_only: false }),
        getProductCategories({ include_hidden: true }),
        getPhotos()
      ]);
      setReviews(reviewsRes.data);
      if (categoriesRes.data?.length) setCategories(categoriesRes.data);
      setPhotos(photosRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      title: "", category: "electronics", brand: "", model_name: "",
      test_context: "", test_duration: "", usage_conditions: "", target_audience: "",
      description: "", rating: 4, value_rating: 4, pros: "", cons: "",
      overall_impression: "", verdict: "", practical_tips: "", alternatives: "",
      price: "", purchase_link: "", is_published: false
    });
    setSelectedPhotos([]);
    setEditingReview(null);
  };

  const handleEdit = (review) => {
    setEditingReview(review);
    setForm({
      title: review.title || "", category: review.category || "electronics",
      brand: review.brand || "", model_name: review.model_name || "",
      test_context: review.test_context || "", test_duration: review.test_duration || "",
      usage_conditions: review.usage_conditions || "", target_audience: review.target_audience || "",
      description: review.description || "", rating: review.rating || 4, value_rating: review.value_rating || 4,
      pros: review.pros?.join("\n") || "", cons: review.cons?.join("\n") || "",
      overall_impression: review.overall_impression || "", verdict: review.verdict || "",
      practical_tips: review.practical_tips || "", alternatives: review.alternatives || "",
      price: review.price || "", purchase_link: review.purchase_link || "",
      is_published: review.is_published || false
    });
    setSelectedPhotos(review.photos?.map(p => p.id) || []);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Le titre est requis"); return; }
    setSaving(true);
    try {
      const data = { ...form, pros: form.pros.split("\n").filter(p => p.trim()), cons: form.cons.split("\n").filter(c => c.trim()), photo_ids: selectedPhotos };
      if (editingReview) { await updateProductReview(editingReview.id, data); toast.success("Test mis à jour !"); }
      else { await createProductReview(data); toast.success("Test créé !"); }
      setShowForm(false); resetForm(); loadData();
    } catch (error) { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce test ?")) return;
    try { await deleteProductReview(id); toast.success("Test supprimé"); loadData(); }
    catch (error) { toast.error("Erreur lors de la suppression"); }
  };

  const togglePublish = async (review) => {
    try { await updateProductReview(review.id, { ...review, is_published: !review.is_published }); toast.success(review.is_published ? "Test dépublié" : "Test publié"); loadData(); }
    catch (error) { toast.error("Erreur lors de la mise à jour"); }
  };

  const togglePhoto = (photoId) => { setSelectedPhotos(prev => prev.includes(photoId) ? prev.filter(id => id !== photoId) : [...prev, photoId]); };

  const getPhotoUrl = (photo, size = 150) => {
    if (photo?.cloudinary_url) return photo.cloudinary_url.replace('/upload/', `/upload/c_fill,g_auto,w_${size},h_${size},f_auto,q_auto/`);
    if (photo?.image_data) return `data:image/jpeg;base64,${photo.image_data}`;
    return null;
  };

  const getCategoryIcon = (cat) => categories.find(c => c.value === cat)?.icon || "📦";
  const getCategoryLabel = (cat) => categories.find(c => c.value === cat)?.label || cat;

  const handleOpenAIEditor = () => {
    if (selectedPhotos.length === 0) { toast.error("Sélectionnez au moins une photo"); return; }
    const selectedPhotoObjects = photos.filter(p => selectedPhotos.includes(p.id));
    navigate("/ai-editor", { state: {
      photos: selectedPhotoObjects, content_type: "product_review", category_name: getCategoryLabel(form.category),
      existingContent: form.title ? { title: form.title, content: form.description, excerpt: form.verdict } : null,
      product_info: { brand: form.brand, model: form.model_name, category: form.category, price: form.price }
    }});
    setShowForm(false);
  };

  const filteredReviews = reviews.filter(review => {
    if (filterCategory !== "all" && review.category !== filterCategory) return false;
    if (filterStatus !== "all" && ((filterStatus === "published" && !review.is_published) || (filterStatus === "draft" && review.is_published))) return false;
    if (searchQuery) { const q = searchQuery.toLowerCase(); return review.title?.toLowerCase().includes(q) || review.brand?.toLowerCase().includes(q); }
    return true;
  });

  const selectedPhotoObjects = photos.filter(p => selectedPhotos.includes(p.id));

  return (
    <div data-testid="product-reviews-admin-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary flex items-center gap-3">
            <Package className="h-8 w-8 text-accent" /> Tests Produits
          </h1>
          <p className="text-muted-foreground mt-1">Partagez vos avis sur les équipements de voyage</p>
        </div>
        <Button className="bg-accent hover:bg-accent/90 gap-2" onClick={() => { setShowForm(true); resetForm(); }}>
          <Plus className="h-4 w-4" /> Nouveau test
        </Button>
      </div>

      <div className="bg-white border rounded-xl p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]"><Input placeholder="Rechercher..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} /></div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Catégorie" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Toutes</SelectItem>{categories.map(c => <SelectItem key={c.value} value={c.value}>{c.icon} {c.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px]"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous</SelectItem><SelectItem value="published">Publiés</SelectItem><SelectItem value="draft">Brouillons</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-auto shadow-2xl">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl font-semibold">{editingReview ? "Modifier le test" : "Nouveau test produit"}</h2>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={handleOpenAIEditor} disabled={selectedPhotos.length === 0}><Sparkles className="h-4 w-4 mr-2" /> IA</Button>
                  <Button variant="ghost" size="icon" onClick={() => { setShowForm(false); resetForm(); }}><X className="h-5 w-5" /></Button>
                </div>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Photos */}
              <div className="border-2 border-dashed rounded-xl p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium flex items-center gap-2"><Camera className="h-4 w-4 text-accent" /> Photos {selectedPhotos.length > 0 && <span className="text-sm bg-accent/10 text-accent px-2 py-0.5 rounded-full">{selectedPhotos.length}</span>}</h3>
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowPhotoLibrary(!showPhotoLibrary)}>{showPhotoLibrary ? "Masquer" : "Bibliothèque"}</Button>
                </div>
                {selectedPhotoObjects.length > 0 && <div className="flex flex-wrap gap-2 mb-4">{selectedPhotoObjects.map(p => <div key={p.id} className="relative group"><img src={getPhotoUrl(p, 100)} alt="" className="h-20 w-20 object-cover rounded-lg border-2 border-accent" /><button type="button" onClick={() => togglePhoto(p.id)} className="absolute -top-1.5 -right-1.5 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100"><X className="h-3 w-3" /></button></div>)}</div>}
                {showPhotoLibrary && <div className="border rounded-lg bg-white max-h-48 overflow-y-auto p-3"><div className="grid grid-cols-6 md:grid-cols-10 gap-2">{photos.map(p => <button key={p.id} type="button" onClick={() => togglePhoto(p.id)} className={`relative aspect-square overflow-hidden rounded-lg border-2 ${selectedPhotos.includes(p.id) ? "border-accent ring-2 ring-accent/30" : "border-transparent hover:border-accent/50"}`}><img src={getPhotoUrl(p, 80)} alt="" className="w-full h-full object-cover" />{selectedPhotos.includes(p.id) && <div className="absolute inset-0 bg-accent/30 flex items-center justify-center"><Check className="h-4 w-4 text-white" /></div>}</button>)}</div></div>}
              </div>

              {/* Infos produit */}
              <div className="space-y-4">
                <h3 className="font-medium text-primary border-b pb-2">Informations produit</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2"><label className="block text-sm font-medium mb-1">Titre *</label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex: Test du Samsonite Lite-Shock" required /></div>
                  <div><label className="block text-sm font-medium mb-1">Catégorie</label><Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{categories.map(c => <SelectItem key={c.value} value={c.value}>{c.icon} {c.label}</SelectItem>)}</SelectContent></Select></div>
                  <div><label className="block text-sm font-medium mb-1">Marque</label><Input value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} /></div>
                  <div><label className="block text-sm font-medium mb-1">Modèle</label><Input value={form.model_name} onChange={e => setForm({ ...form, model_name: e.target.value })} /></div>
                  <div><label className="block text-sm font-medium mb-1">Prix</label><Input value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="299€" /></div>
                </div>
              </div>

              {/* Contexte */}
              <div className="space-y-4">
                <h3 className="font-medium text-primary border-b pb-2 flex items-center gap-2"><Clock className="h-4 w-4" /> Contexte de test</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Contexte</label><Textarea value={form.test_context} onChange={e => setForm({ ...form, test_context: e.target.value })} rows={2} /></div>
                  <div><label className="block text-sm font-medium mb-1">Durée du test</label><Input value={form.test_duration} onChange={e => setForm({ ...form, test_duration: e.target.value })} /></div>
                  <div><label className="block text-sm font-medium mb-1">Conditions</label><Textarea value={form.usage_conditions} onChange={e => setForm({ ...form, usage_conditions: e.target.value })} rows={2} /></div>
                  <div><label className="block text-sm font-medium mb-1">Public visé</label><Input value={form.target_audience} onChange={e => setForm({ ...form, target_audience: e.target.value })} /></div>
                </div>
              </div>

              <div><label className="block text-sm font-medium mb-1">Description</label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={4} /></div>

              {/* Notes */}
              <div className="space-y-4">
                <h3 className="font-medium text-primary border-b pb-2 flex items-center gap-2"><Star className="h-4 w-4" /> Évaluation</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div><label className="block text-sm font-medium mb-3">Note : {form.rating}/5</label><div className="flex gap-2">{[1,2,3,4,5].map(s => <button key={s} type="button" onClick={() => setForm({ ...form, rating: s })}><Star className={`h-8 w-8 ${s <= form.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}/></button>)}</div></div>
                  <div><label className="block text-sm font-medium mb-3">Qualité/Prix : {form.value_rating}/5</label><div className="flex gap-2">{[1,2,3,4,5].map(s => <button key={s} type="button" onClick={() => setForm({ ...form, value_rating: s })}><DollarSign className={`h-6 w-6 ${s <= form.value_rating ? 'text-green-500' : 'text-gray-300'}`}/></button>)}</div></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1 flex items-center gap-1"><ThumbsUp className="h-4 w-4 text-green-500" /> Points forts</label><Textarea value={form.pros} onChange={e => setForm({ ...form, pros: e.target.value })} rows={4} /></div>
                  <div><label className="block text-sm font-medium mb-1 flex items-center gap-1"><ThumbsDown className="h-4 w-4 text-red-500" /> Points faibles</label><Textarea value={form.cons} onChange={e => setForm({ ...form, cons: e.target.value })} rows={4} /></div>
                </div>
              </div>

              {/* Verdict */}
              <div className="space-y-4">
                <h3 className="font-medium text-primary border-b pb-2 flex items-center gap-2"><Award className="h-4 w-4" /> Verdict</h3>
                <div><label className="block text-sm font-medium mb-1">Ressenti global</label><Textarea value={form.overall_impression} onChange={e => setForm({ ...form, overall_impression: e.target.value })} rows={3} /></div>
                <div><label className="block text-sm font-medium mb-1">Conclusion</label><Textarea value={form.verdict} onChange={e => setForm({ ...form, verdict: e.target.value })} rows={3} /></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Conseils</label><Textarea value={form.practical_tips} onChange={e => setForm({ ...form, practical_tips: e.target.value })} rows={2} /></div>
                  <div><label className="block text-sm font-medium mb-1">Alternatives</label><Textarea value={form.alternatives} onChange={e => setForm({ ...form, alternatives: e.target.value })} rows={2} /></div>
                </div>
              </div>

              <div><label className="block text-sm font-medium mb-1">Lien d'achat</label><Input value={form.purchase_link} onChange={e => setForm({ ...form, purchase_link: e.target.value })} /></div>

              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <Switch checked={form.is_published} onCheckedChange={v => setForm({ ...form, is_published: v })} />
                <div><p className="font-medium text-sm">Publier</p><p className="text-xs text-muted-foreground">Rendre visible sur le site</p></div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Annuler</Button>
                <Button type="submit" className="bg-accent hover:bg-accent/90" disabled={saving}>
                  {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Enregistrement...</> : <><Check className="h-4 w-4 mr-2" /> {editingReview ? "Mettre à jour" : "Créer"}</>}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">{Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
      ) : filteredReviews.length > 0 ? (
        <div className="space-y-4">
          {filteredReviews.map(review => {
            const isExpanded = expandedReview === review.id;
            return (
              <div key={review.id} className={`bg-white border rounded-xl overflow-hidden hover:shadow-md ${!review.is_published ? "opacity-80" : ""}`}>
                <div className="p-4"><div className="flex items-start gap-4">
                  {review.photos?.[0] && <div className="shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-gray-100"><img src={getPhotoUrl(review.photos[0], 200)} alt="" className="w-full h-full object-cover" /></div>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-lg">{getCategoryIcon(review.category)}</span>
                      <h3 className="font-heading text-lg font-semibold text-primary">{review.title}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${review.is_published ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{review.is_published ? "Publié" : "Brouillon"}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground mb-2">
                      {review.brand && <span className="font-medium">{review.brand}</span>}
                      {review.model_name && <span>{review.model_name}</span>}
                      {review.price && <span className="text-accent font-medium">{review.price}</span>}
                    </div>
                    <div className="flex items-center gap-1">{[1,2,3,4,5].map(s => <Star key={s} className={`h-4 w-4 ${s <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />)}<span className="text-sm text-muted-foreground ml-2">{review.rating}/5</span></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => setExpandedReview(isExpanded ? null : review.id)}>{isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</Button>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(review)}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(review.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div></div>
                {isExpanded && (
                  <div className="border-t bg-gray-50 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {review.pros?.length > 0 && <div><h4 className="font-medium text-sm mb-2 flex items-center gap-1 text-green-600"><ThumbsUp className="h-4 w-4" /> Points forts</h4><ul className="text-sm space-y-1">{review.pros.map((p, i) => <li key={i} className="flex items-start gap-2"><Check className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />{p}</li>)}</ul></div>}
                      {review.cons?.length > 0 && <div><h4 className="font-medium text-sm mb-2 flex items-center gap-1 text-red-600"><ThumbsDown className="h-4 w-4" /> Points faibles</h4><ul className="text-sm space-y-1">{review.cons.map((c, i) => <li key={i} className="flex items-start gap-2"><X className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />{c}</li>)}</ul></div>}
                    </div>
                    {review.verdict && <div className="mb-4 p-3 bg-white rounded-lg border"><h4 className="font-medium text-sm mb-1 flex items-center gap-1"><Award className="h-4 w-4 text-accent" /> Verdict</h4><p className="text-sm text-muted-foreground">{review.verdict}</p></div>}
                    <div className="flex items-center gap-4 pt-2 border-t">
                      <Button variant={review.is_published ? "outline" : "default"} size="sm" onClick={() => togglePublish(review)} className={review.is_published ? "" : "bg-green-600 hover:bg-green-700"}>{review.is_published ? "Dépublier" : "Publier"}</Button>
                      {review.purchase_link && <a href={review.purchase_link} target="_blank" rel="noopener noreferrer" className="text-sm text-accent hover:underline flex items-center gap-1"><ExternalLink className="h-4 w-4" /> Lien d'achat</a>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 bg-white border rounded-xl">
          <Package className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-semibold text-primary mb-2">Aucun test produit</h3>
          <p className="text-muted-foreground mb-6">{searchQuery || filterCategory !== "all" || filterStatus !== "all" ? "Aucun résultat pour ces filtres" : "Partagez votre premier test"}</p>
          {!searchQuery && filterCategory === "all" && filterStatus === "all" && <Button onClick={() => { setShowForm(true); resetForm(); }} className="bg-accent hover:bg-accent/90"><Plus className="h-4 w-4 mr-2" /> Nouveau test</Button>}
        </div>
      )}
    </div>
  );
}
